
import React from 'react';
import { Check, Zap, User } from 'lucide-react';
import { content } from '../data/content';

interface ProductsProps {
  onContactClick: () => void;
}

const Products: React.FC<ProductsProps> = ({ onContactClick }) => {
  const { products } = content;

  return (
    <section className="min-h-screen flex items-center justify-center py-24 bg-gray-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-[600px] h-[600px] bg-blue-50 rounded-full blur-[100px] mix-blend-multiply"></div>
        <div className="absolute bottom-1/4 -right-20 w-[600px] h-[600px] bg-purple-50 rounded-full blur-[100px] mix-blend-multiply"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 max-w-6xl mx-auto items-stretch">
          
          {/* Personal Subscription - Metallic Champagne Gold */}
          <div className="relative rounded-[2.5rem] p-10 shadow-[0_25px_60px_rgba(140,115,85,0.5)] transition-all duration-500 group flex flex-col overflow-hidden hover:transform hover:-translate-y-1 border border-white/40">
            {/* Metallic Background Gradient - Flowing Effect (Top-Left to Bottom-Right) */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#8c7355] via-[#f7e7ce] via-[#ffffff] via-[#f7e7ce] to-[#5c4d3c] bg-[length:400%_400%] animate-gradient-slow"></div>
            
            {/* Metallic Texture Overlay - Heavy Gravity */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/40 mix-blend-overlay"></div>
            
            {/* Light Gradient Spotlight - Strong */}
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/60 rounded-full blur-[80px] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none mix-blend-overlay"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

            {/* Metallic Sheen/Highlight - Strong */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent opacity-60"></div>
            
            {/* Shine Animation - Sweeping Light Effect (Strong) */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/90 to-transparent -skew-x-12 translate-x-[-200%] animate-shine pointer-events-none w-[200%] mix-blend-overlay opacity-100"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -skew-x-12 translate-x-[-200%] animate-shine pointer-events-none w-[200%] animation-delay-500 opacity-80"></div>
            
            {/* Flashing Lights / Sparkles Effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Sparkle 1 - Top Right */}
              <div className="absolute top-12 right-12 w-1 h-8 bg-white blur-[1px] animate-pulse opacity-80 mix-blend-overlay rotate-45"></div>
              <div className="absolute top-12 right-12 w-8 h-1 bg-white blur-[1px] animate-pulse opacity-80 mix-blend-overlay rotate-45"></div>
              <div className="absolute top-12 right-12 w-4 h-4 bg-white rounded-full blur-[4px] animate-pulse opacity-90 mix-blend-overlay"></div>
              
              {/* Sparkle 2 - Bottom Left */}
              <div className="absolute bottom-24 left-12 w-1 h-6 bg-white blur-[1px] animate-pulse opacity-60 mix-blend-overlay rotate-45 animation-delay-1000"></div>
              <div className="absolute bottom-24 left-12 w-6 h-1 bg-white blur-[1px] animate-pulse opacity-60 mix-blend-overlay rotate-45 animation-delay-1000"></div>
              
              {/* Random Glints */}
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute bg-white rounded-full animate-twinkle"
                  style={{
                    width: Math.random() * 3 + 1 + 'px',
                    height: Math.random() * 3 + 1 + 'px',
                    top: Math.random() * 100 + '%',
                    left: Math.random() * 100 + '%',
                    animationDelay: Math.random() * 3 + 's',
                    opacity: Math.random() * 0.8 + 0.2,
                    boxShadow: '0 0 8px 1px rgba(255, 255, 255, 0.9)'
                  }}
                ></div>
              ))}
            </div>
            
            {/* Top Highlight Border */}
            <div className="absolute top-0 left-0 w-full h-[1px] bg-white/80"></div>
            <div className="absolute bottom-0 left-0 w-full h-[1px] bg-[#8c7355]/20"></div>

            <div className="relative z-10">
              <div className="mb-8 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-white/60 rounded-xl mb-4 text-[#8c7355] shadow-sm backdrop-blur-sm border border-white/50">
                  <User className="w-6 h-6" />
                </div>
                <h3 className="text-3xl font-bold text-[#5c4d3c] mb-2 drop-shadow-sm">{products.items[0].title}</h3>
                <p className="text-[#7a6b5a] font-medium text-lg mb-6">{products.items[0].subtitle}</p>
                
                <div className="inline-flex items-center justify-center px-4 py-1.5 bg-white/40 rounded-full border border-white/40 shadow-inner backdrop-blur-sm">
                  <span className="text-[#5c4d3c] font-bold text-sm">
                    {products.items[0].tags?.join(' ｜ ')}
                  </span>
                </div>
              </div>

              <div className="flex-grow space-y-8">
                {/* We Deliver */}
                <div className="bg-white/40 rounded-2xl p-6 border border-white/50 shadow-sm backdrop-blur-sm">
                  <h4 className="font-bold text-[#4a3b2a] mb-4 flex items-center text-lg">
                    <Zap className="w-5 h-5 text-[#8c7355] mr-2 fill-current" />
                    我们交付
                  </h4>
                  <ul className="grid grid-cols-1 gap-3">
                    {products.items[0].weDo?.map((item, idx) => (
                      <li key={idx} className="flex items-start text-[#5c4d3c] text-sm font-medium">
                        <Check className="text-[#8c7355] mr-3 w-4 h-4 mt-0.5 flex-shrink-0" /> 
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* You Cooperate */}
                <div className="px-2">
                  <h4 className="font-bold text-[#8c7a6b] mb-3 flex items-center text-xs uppercase tracking-wider">
                    你们配合
                  </h4>
                  <ul className="space-y-2">
                    {products.items[0].youDo?.map((item, idx) => (
                      <li key={idx} className="flex items-start text-[#7a6b5a] text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#b0a090] mr-3 mt-1.5 shadow-[0_1px_2px_rgba(0,0,0,0.1)]"></div>
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8">
                <button 
                  onClick={onContactClick}
                  className="w-full py-4 bg-gradient-to-b from-white to-[#f0e6d8] border border-white text-[#5c4d3c] rounded-xl font-bold text-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center group/btn shadow-[0_4px_10px_rgba(140,115,85,0.2)]"
                >
                  {products.items[0].cta}
                </button>
              </div>
            </div>
          </div>

          {/* Team Subscription - Colorful Galaxy with Massive Stars */}
          <div className="bg-slate-900 rounded-[2.5rem] p-10 shadow-[0_30px_80px_rgba(15,23,42,0.6)] border border-white/10 transition-all duration-500 group flex flex-col relative overflow-hidden animate-float">
            {/* Galaxy Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-900 via-slate-900 to-black"></div>
              <div className="absolute bottom-0 left-0 w-full h-full bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-blue-900/50 via-transparent to-transparent"></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-[radial-gradient(circle,_var(--tw-gradient-stops))] from-pink-500/10 via-transparent to-transparent blur-3xl"></div>
              
              {/* Massive Stars */}
              {Array.from({ length: 350 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute bg-white rounded-full animate-twinkle"
                  style={{
                    width: Math.random() * 2 + 0.5 + 'px',
                    height: Math.random() * 2 + 0.5 + 'px',
                    top: Math.random() * 100 + '%',
                    left: Math.random() * 100 + '%',
                    animationDelay: Math.random() * 5 + 's',
                    opacity: Math.random() * 0.8 + 0.2,
                    boxShadow: Math.random() > 0.9 ? '0 0 4px 1px rgba(255, 255, 255, 0.8)' : 'none'
                  }}
                ></div>
              ))}
            </div>
            
            <div className="relative z-10 mb-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-xl mb-4 backdrop-blur-sm border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.2)]">
                <Zap className="w-6 h-6 text-purple-300 fill-current" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-2 drop-shadow-md">{products.items[1].title}</h3>
              <p className="text-purple-200 font-medium text-lg mb-6">{products.items[1].subtitle}</p>
              
              <div className="inline-flex items-center justify-center px-4 py-1.5 bg-white/10 rounded-full backdrop-blur-sm border border-white/20 shadow-inner">
                <span className="text-purple-100 font-bold text-sm">
                  {products.items[1].tags?.join(' ｜ ')}
                </span>
              </div>
            </div>

            <div className="relative z-10 flex-grow space-y-8">
              {/* We Deliver */}
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10 backdrop-blur-md shadow-lg">
                <h4 className="font-bold text-white mb-4 flex items-center text-lg">
                  <Zap className="w-5 h-5 text-purple-400 mr-2 fill-current" />
                  我们交付
                </h4>
                <ul className="grid grid-cols-1 gap-3">
                  {products.items[1].weDo?.map((item, idx) => (
                    <li key={idx} className="flex items-start text-purple-100 text-sm font-medium">
                      <Check className="text-purple-400 mr-3 w-4 h-4 mt-0.5 flex-shrink-0" /> 
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* You Cooperate */}
              <div className="px-2">
                <h4 className="font-bold text-purple-300/70 mb-3 flex items-center text-xs uppercase tracking-wider">
                  你们配合
                </h4>
                <ul className="space-y-2">
                  {products.items[1].youDo?.map((item, idx) => (
                    <li key={idx} className="flex items-start text-purple-200/80 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-3 mt-1.5 shadow-[0_0_5px_rgba(168,85,247,0.5)]"></div>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="relative z-10 mt-8">
              <button 
                onClick={onContactClick}
                className="w-full py-4 bg-gradient-to-b from-purple-500 to-purple-700 text-white rounded-xl font-bold text-lg shadow-[0_6px_0_#4c1d95] active:shadow-none active:translate-y-[6px] transition-all duration-150 border-t border-white/30 hover:brightness-110 relative overflow-hidden group/btn"
              >
                <span className="relative z-10 drop-shadow-md">{products.items[1].cta}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shine"></div>
              </button>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Products;
